import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { makeStyles } from '@mui/styles'
import MetricsPanel from '../panels/MetricsPanel'
import NoSite from '../components/NoSite'
import { Box, Tab, Typography, Grid, CircularProgress } from '@mui/material'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import MetricsTimeSeriesChart from '../charts/Metrics/MetricsTimeSeriesChart'
import Parameters from '../components/MetricsParameter'
import MetricsResidulChart from '../charts/Metrics/MetricsResidualChart'
import MetricsOnlineChart from '../charts/Metrics/MetricsOnlineChart'
import Api from '../api'
import { setSelectedUserSetup } from '../redux/actions/userSetups'
import { getDefaultMetricsTab } from '../utils/metricsTabs'

const ONLINE_FILTERED_KEYS = [
  'OnlineNmaeFiltered',
  'OnlineNrmseFiltered',
  'OnlineNBiasFiltered',
  'OnlineMaeFiltered',
  'OnlineRmseFiltered',
  'OnlineBiasFiltered',
]

const METERING_FILTERED_KEYS = [
  'MeteringNmaeFiltered',
  'MeteringNrmseFiltered',
  'MeteringNBiasFiltered',
  'MeteringMaeFiltered',
  'MeteringRmseFiltered',
  'MeteringBiasFiltered',
]

const useStyles = makeStyles(theme => ({
  chartContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
  },
  subCaption: {
    color: theme.palette.primary.main,
    fontSize: '20px',
    fontWeight: '600',
  },
  paramContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '10px 0px 10px',
  },
  centerItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
  },
}))

const MetricsPage = props => {
  const classes = useStyles(props)
  const {
    container,
    selectedSetup,
    setSelectedUserSetup,
    unit,
    timezone,
    selectedSetupPlant,
    selectedForecastType,
    issueTime,
    userPowerSetups,
  } = props

  // Use userPowerSetups from Redux, validate selectedSetup locally
  const userSetups = userPowerSetups
  const validatedSetup = userPowerSetups.includes(selectedSetup)
    ? selectedSetup
    : null

  // If there is only 1 available setup, select it automatically
  useEffect(() => {
    if (userSetups.length === 1 && selectedSetup !== userSetups[0]) {
      setSelectedUserSetup(userSetups[0])
    }
  }, [userSetups, selectedSetup, setSelectedUserSetup])

  const [loading, setLoading] = useState(false)
  const [mainTabValue, setMainTabValue] = useState('1')
  const [subTabValue, setSubTabValue] = useState('0')

  const [metricsData, setMetricsData] = useState(null)
  const [selectedPlant, setSelectedPlant] = useState(null)
  const disableButton = metricsData !== null || loading ? true : false

  const [displayChart, setDisplayChart] = useState([true, false, false])
  const [visibleChart, setVisibleChart] = useState(0)
  const [selectedForecast, setSelectedForecast] = useState(null)

  // for disabling inputs in form
  const [trackDisableStatus, setTrackDisableStatus] = useState(false)

  let initNodata = {
    heading: 'No setup selected',
    body: 'Select a setup and adjust parameters in order to start metrics',
  }

  if (validatedSetup) {
    initNodata = {
      heading: '',
      body: '',
    }
  }

  const [nodataContent, setNodataContent] = useState(initNodata)

  const handleMainTabChange = (_, newValue) => {
    setMainTabValue(newValue)
  }

  const handleSubTabChange = (_, newValue) => {
    const newState = [...displayChart]
    newState[newValue] = true
    setDisplayChart(newState)
    setVisibleChart(newValue)
    setSubTabValue(newValue)
  }

  const handlePlantChange = newPlant => {
    setSelectedPlant(newPlant)
  }

  const startMetricsData = async metricsObj => {
    setLoading(true)
    setTrackDisableStatus(true)
    try {
      const response = await Api.Metrics.getMetrics(metricsObj)
      if (response.status === 204) {
        setMetricsData(null)
        setTrackDisableStatus(false)
        setNodataContent({
          heading: `No data for plant ${selectedSetupPlant || 'Unknown'}`,
          body: '',
        })
      }

      if (response.status === 200) {
        const metrics = response?.data?.data

        if (!response.data || !metrics) {
          setNodataContent({
            heading: `Data issue for setup: ${selectedSetup}`,
            body: 'We received an invalid dataset from the server.',
          })
          setMetricsData(null)
          setTrackDisableStatus(false)
        } else {
          setMainTabValue(getDefaultMetricsTab(response.data))
          setMetricsData(response.data)
        }
      }

      setLoading(false)
    } catch (error) {
      setMetricsData(null)
      setTrackDisableStatus(false)
      if (error.response.status === 411) {
        setNodataContent({
          heading: `Error running query for selected setup: ${selectedSetup}`,
          body: '',
        })
      } else if (error.response.status === 404) {
        setNodataContent({
          heading: `No data for selected period. Please check your selection.`,
          body: '',
        })
      } else {
        setNodataContent({
          heading: `Error fetching data for selected setup: ${selectedSetup}`,
          body: '',
        })
      }
      console.error('Error fetching Metrics data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleStartMetric = metricsObj => {
    startMetricsData(metricsObj)
  }

  const handleNoDataContentUpdate = newContent => {
    setNodataContent(newContent)
    setMetricsData(null)
  }

  /** This code will activate reset metricsData if user change the unit or timezone inside Logout component */
  useEffect(() => {
    setMetricsData(null)
  }, [timezone, unit])

  /*
  This function is separated from the tab context so that you don't have 
  to render the charts again while switching between tabs
  */

  const panels = () => {
    return (
      <>
        {displayChart[0] && (
          <div
            value="0"
            style={{
              padding: '0',
              display: visibleChart == '0' ? 'block' : 'none',
            }}
          >
            <Grid container spacing={0}>
              <Grid item md={12}>
                <MetricsTimeSeriesChart
                  chartDataRaw={metricsData['data']}
                  resolution={metricsData.resolution}
                  chartName={'chartMetricsTimeSeries'}
                  selectedSetup={selectedSetup}
                  selectedPlant={selectedSetupPlant}
                  selectedForecastType={selectedForecastType}
                  setTrackDisableStatus={setTrackDisableStatus}
                  issueTime={issueTime}
                  chartType={'metrics'}
                />
              </Grid>
            </Grid>
          </div>
        )}

        {displayChart[1] && (
          <div
            value="1"
            style={{
              padding: '0',
              display: visibleChart == '1' ? 'block' : 'none',
            }}
          >
            {mainTabValue === '1' ? (
              <Grid container spacing={0} key={mainTabValue}>
                <Grid item md={6}>
                  <MetricsResidulChart
                    chartData={metricsData['data']}
                    chartName={'chartMetricsResidualOnline'}
                    chartType={'online'}
                    setTrackDisableStatus={setTrackDisableStatus}
                  ></MetricsResidulChart>
                </Grid>
                <Grid item md={6}>
                  <MetricsOnlineChart
                    chartData={metricsData['data']}
                    chartName={'chartMetricsOnlineOnline'}
                    chartType={'online'}
                    setTrackDisableStatus={setTrackDisableStatus}
                  ></MetricsOnlineChart>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={0} key={mainTabValue}>
                <Grid item md={6}>
                  <MetricsResidulChart
                    chartData={metricsData['data']}
                    chartName={'chartMetricsResidualMetering'}
                    chartType={'metering'}
                    setTrackDisableStatus={setTrackDisableStatus}
                  ></MetricsResidulChart>
                </Grid>
                <Grid item md={6}>
                  <MetricsOnlineChart
                    chartData={metricsData['data']}
                    chartName={'chartMetricsOnlineMetering'}
                    chartType={'metering'}
                    setTrackDisableStatus={setTrackDisableStatus}
                  ></MetricsOnlineChart>
                </Grid>
              </Grid>
            )}
          </div>
        )}
        {/* {displayChart[2] && (
          <div
            value="2"
            style={{ display: visibleChart == '2' ? 'block' : 'none' }}
          >
            <Grid container spacing={0}>
              <Grid item md={12}>
                <MetricsTimeSeriesChart
                  chartData={metricsData}
                  resolution={metricsData.resolution}
                  chartName={'chartMetricsTimeSeries33'}
                />
              </Grid>
            </Grid>
          </div>
        )} */}
      </>
    )
  }

  const resultsCharts = () => {
    return (
      <>
        <Typography className={classes.subCaption}>
          FORECAST VS. OBSERVATIONS
        </Typography>
        <TabContext value={subTabValue}>
          <Box sx={{ borderBottom: 0 }}>
            <TabList
              onChange={handleSubTabChange}
              aria-label="sub tabs for Online"
              sx={{
                '& .MuiTabs-indicator': { backgroundColor: '#368ED8' },
                '& .MuiTab-root.Mui-selected': { color: '#368ED8' },
                '& .MuiTab-root': {
                  minWidth: '160px',
                },
              }}
            >
              <Tab label="Time Series Plot" value="0" />
              <Tab label="Residual Plot" value="1" />
              {/* {selectedForecast === 'aggregated' && (
                <Tab label="Coverage Plot" value="2" />
              )} */}
            </TabList>
          </Box>
        </TabContext>
        {panels()}
      </>
    )
  }

  const resultPanel = () => {
    const hasOnlineFiltered =
      metricsData !== null && ONLINE_FILTERED_KEYS.every(k => k in metricsData)
    const hasMeteringFiltered =
      metricsData !== null &&
      METERING_FILTERED_KEYS.every(k => k in metricsData)

    return metricsData !== null ? (
      <>
        <TabContext value={mainTabValue}>
          <Box sx={{ borderBottom: 0 }}>
            <TabList
              onChange={handleMainTabChange}
              aria-label="Metrics charts"
              sx={{
                '& .MuiTabs-indicator': { backgroundColor: '#368ED8' },
                '& .MuiTab-root.Mui-selected': { color: '#368ED8' },
                '& .MuiTab-root': {
                  minWidth: '160px',
                },
              }}
            >
              <Tab label="Online" value="1" />
              <Tab label="Metering" value="2" />
            </TabList>
          </Box>

          <TabPanel value="1" sx={{ p: 0 }}>
            <div className={classes.paramContainer}>
              <Parameters
                label={'nmae'}
                value={metricsData.OnlineNmae}
                normalizedValue={metricsData?.AvailableCapacity}
              />
              <Parameters label={'nrmse'} value={metricsData.OnlineNrmse} />
              <Parameters label={'nbias'} value={metricsData.OnlineNBias} />
              <Parameters label={'mae'} value={metricsData.OnlineMae} />
              <Parameters label={'rmse'} value={metricsData.OnlineRmse} />
              <Parameters label={'bias'} value={metricsData.OnlineBias} />
            </div>
            {hasOnlineFiltered && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  {/* <Typography variant="caption" color="text.secondary">
                    filtered
                  </Typography> */}
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                </Box>
                <div className={classes.paramContainer}>
                  <Parameters
                    small
                    label={'nmae'}
                    value={metricsData.OnlineNmaeFiltered}
                  />
                  <Parameters
                    small
                    label={'nrmse'}
                    value={metricsData.OnlineNrmseFiltered}
                  />
                  <Parameters
                    small
                    label={'nbias'}
                    value={metricsData.OnlineNBiasFiltered}
                  />
                  <Parameters
                    small
                    label={'mae'}
                    value={metricsData.OnlineMaeFiltered}
                  />
                  <Parameters
                    small
                    label={'rmse'}
                    value={metricsData.OnlineRmseFiltered}
                  />
                  <Parameters
                    small
                    label={'bias'}
                    value={metricsData.OnlineBiasFiltered}
                  />
                </div>
              </>
            )}
          </TabPanel>

          <TabPanel value="2" sx={{ p: 0 }}>
            <div className={classes.paramContainer}>
              <Parameters label={'nmae'} value={metricsData.MeteringNmae} />
              <Parameters label={'nrmse'} value={metricsData.MeteringNrmse} />
              <Parameters label={'nbias'} value={metricsData.MeteringNBias} />
              <Parameters label={'mae'} value={metricsData.MeteringMae} />
              <Parameters label={'rmse'} value={metricsData.MeteringRmse} />
              <Parameters label={'bias'} value={metricsData.MeteringBias} />
            </div>
            {hasMeteringFiltered && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    filtered
                  </Typography>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                </Box>
                <div className={classes.paramContainer}>
                  <Parameters
                    small
                    label={'nmae'}
                    value={metricsData.MeteringNmaeFiltered}
                  />
                  <Parameters
                    small
                    label={'nrmse'}
                    value={metricsData.MeteringNrmseFiltered}
                  />
                  <Parameters
                    small
                    label={'nbias'}
                    value={metricsData.MeteringNBiasFiltered}
                  />
                  <Parameters
                    small
                    label={'mae'}
                    value={metricsData.MeteringMaeFiltered}
                  />
                  <Parameters
                    small
                    label={'rmse'}
                    value={metricsData.MeteringRmseFiltered}
                  />
                  <Parameters
                    small
                    label={'bias'}
                    value={metricsData.MeteringBiasFiltered}
                  />
                </div>
              </>
            )}
          </TabPanel>
        </TabContext>
        {resultsCharts()}
      </>
    ) : (
      <NoSite
        {...props}
        creating={false}
        heading={nodataContent.heading}
        body={nodataContent.body}
      ></NoSite>
    )
  }

  return (
    <>
      <MetricsPanel
        container={container}
        userSetups={userSetups}
        selectedPlant={selectedPlant}
        setSelectedPlant={handlePlantChange}
        setMetricsParameters={handleStartMetric}
        handleNoDataContentUpdate={handleNoDataContentUpdate}
        setMetricsData={setMetricsData}
        onChangeSelectedForecast={selVal => setSelectedForecast(selVal.name)}
        disableButton={disableButton}
        trackDisableStatus={trackDisableStatus}
      >
        {!loading ? (
          resultPanel()
        ) : (
          <div className={classes.centerItem}>
            <CircularProgress />
          </div>
        )}
      </MetricsPanel>
    </>
  )
}

const mapStateToProps = state => ({
  selectedSetup: state.userSetups.selectedSetup,
  userPowerSetups: state.userSetups.userPowerSetups,
  unit: state.user.unit,
  timezone: state.user.timezone,
  selectedSetupPlant: state.metrics.selectedPlant,
  selectedForecastType: state.metrics.selectedForecastType,
  issueTime: state.metrics.issueTime,
})

const mapDispatchToProps = {
  setSelectedUserSetup,
}
export default connect(mapStateToProps, mapDispatchToProps)(MetricsPage)
